import { ReactNode, forwardRef } from 'react';
import cn from 'classnames';
import { Badge, type BadgeProps } from '@/ui-components/atoms/Badge/Badge';
import { Link } from '@/ui-components/atoms/Link/Link';
import styles from './entityListItem.module.scss';
import { ChannelBadge } from '../ChannelCardStrip/ChannelBadge';

const {
    contentWrapper,
    imageWrapper,
    eyebrowStyles,
    eyebrowAndBadgeWrapper,
    titleStyles,
    doubleLineTitleStyles,
    shortDescriptionStyles,
    linkStyles,
    lightTheme,
    textWrapperStyles,
    bottomMargin,
    smallVariant,
    badgeInEntityHero,
} = styles;

export enum Theme {
    light = 'light',
}

export enum Variant {
    small = 'small',
    large = 'large',
}

export interface EntityListItemProps {
    title: string | ReactNode;
    badge?: BadgeProps;
    isAppOnlyBadge?: boolean;
    isXLBadge?: boolean;
    eyebrow?: string;
    shortDescription?: ReactNode;
    image?: ReactNode;
    href?: string;
    dataCy?: string;
    theme?: Theme.light;
    className?: string;
    hideImage?: boolean;
    verticalSpacing?: boolean;
    variant?: Variant;
    onClick?: React.MouseEventHandler;
    isInEntityHero?: boolean;
}

export const EntityListItem = forwardRef<HTMLAnchorElement, EntityListItemProps>(
    (
        {
            title,
            badge,
            isAppOnlyBadge,
            isXLBadge,
            eyebrow,
            shortDescription,
            image,
            href,
            dataCy,
            theme,
            className,
            hideImage = false,
            verticalSpacing = false,
            variant = Variant.large,
            onClick,
            isInEntityHero = false,
        },
        ref,
    ) => {
        const contentStyling = cn(className, {
            [contentWrapper]: theme !== 'light',
            [lightTheme]: theme === 'light',
            [bottomMargin]: verticalSpacing,
            [smallVariant]: variant === 'small',
        });

        const content = (
            <div className={contentStyling} data-cy={dataCy || 'atoms-entityListItem'}>
                {!hideImage && image && (
                    <div aria-hidden="true" className={imageWrapper}>
                        {image}
                    </div>
                )}
                <div className={textWrapperStyles}>
                    {(eyebrow || isAppOnlyBadge || isXLBadge || badge) && (
                        <div className={eyebrowAndBadgeWrapper}>
                            {eyebrow && (
                                <div className={eyebrowStyles}>
                                    {isNaN(Number(eyebrow)) ? eyebrow : `CH ${Number(eyebrow)}`}
                                </div>
                            )}
                            {badge && <Badge {...badge} />}
                            {isXLBadge && (
                                <ChannelBadge
                                    badgeText={'Explicit'}
                                    className={isInEntityHero ? badgeInEntityHero : ''}
                                />
                            )}
                            {isAppOnlyBadge && (
                                <ChannelBadge
                                    badgeText={'APP ONLY'}
                                    className={isInEntityHero ? badgeInEntityHero : ''}
                                />
                            )}
                        </div>
                    )}

                    <h3 className={(!eyebrow && !badge) || !shortDescription ? doubleLineTitleStyles : titleStyles}>
                        {title}
                    </h3>
                    {shortDescription && <div className={shortDescriptionStyles}>{shortDescription}</div>}
                </div>
            </div>
        );

        if (href) {
            const contentStyling = cn(linkStyles, {
                [bottomMargin]: verticalSpacing,
            });

            return (
                <Link href={href} className={contentStyling} ref={ref} onClick={onClick}>
                    {content}
                </Link>
            );
        }

        return <>{content}</>;
    },
);

EntityListItem.displayName = 'EntityListItem';
